import React, { memo } from 'react';
import { motion } from 'framer-motion';
import DynamicIcon from './DynamicIcon';
import DownloadButton from './DownloadButton';
import { formatBytes } from '../utils/formatBytes';

const formatModifiedDate = (date) => {
  if (!date) return 'Unknown';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return 'Unknown';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsedDate);
};

const FileItem = memo(({ file, darkMode, onClick, onDownload, isGridView, showActionsColumn, isLatest, variants }) => {
  const isFolder = file.isFolder;
  const iconName = isFolder ? 'folder' : 'file';
  const isImageBuild = !isFolder && /\.img$/i.test(file.name);
  const isOtaBuild = !isFolder && /OTA.*\d{8}[-_]\d{4}.*\.zip$/i.test(file.name);
  const isDatedBuild = isOtaBuild || isImageBuild;
  const displayedDate = isImageBuild ? file.uploadedDateTime : file.fileDateTime;
  const dateLabel = isImageBuild ? 'Upload date' : 'Release date';

  const getFileExtension = (filename) => {
    const parts = filename.split('.');
    if (parts.length > 1) {
        return parts.pop().toUpperCase();
    }
    return 'FILE';
  };

  if (isGridView) {
    return (
      <motion.li
        variants={variants}
        layout="position"
        className="relative rounded-2xl flex flex-col h-full shadow-md shadow-black/10"
      >
        <motion.button
          whileHover={{ y: -5, scale: 1.03, boxShadow: "0px 10px 20px rgba(0,0,0,0.1)" }}
          whileTap={{ scale: 0.98, y: -2 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          onClick={() => isFolder && onClick(file)}
          aria-label={isFolder ? `Open folder ${file.name}` : `View file ${file.name}`}
          className={`p-5 h-full w-full text-left rounded-2xl flex flex-col ${darkMode ? 'bg-[#1a1823]' : 'bg-white'}`}
        >
          <div className="flex-1 flex flex-col">
            <div className="flex items-start mb-4">
              <div className={`p-3.5 rounded-xl ${darkMode ? (isFolder ? 'bg-blue-500/10' : 'bg-cyan-500/10') : (isFolder ? 'bg-blue-100' : 'bg-cyan-100')}`}>
                <DynamicIcon name={iconName} darkMode={darkMode} className="h-6 w-6" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <h3 className={`font-medium whitespace-normal break-all leading-snug ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                  {file.name}
                </h3>
                {isLatest && (
                  <span className="inline-flex mt-1 px-2 py-1 rounded bg-[#20c747] text-[#07130a] text-xs font-semibold">
                    LATEST
                  </span>
                )}
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {formatBytes(file.size)}
                </p>
                {isDatedBuild && (
                  <div className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <p>{dateLabel}: {formatModifiedDate(displayedDate)}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-auto pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
              {!isFolder && (
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {getFileExtension(file.name)}
                  </span>
                  <DownloadButton
                    darkMode={darkMode}
                    onClick={onDownload}
                    className={`
                      h-9 w-9 flex items-center justify-center rounded-full transition-colors
                      ${darkMode ? 'bg-slate-700 hover:bg-cyan-400' : 'bg-slate-200 hover:bg-cyan-500'}
                      hover:text-white
                    `}
                  />
                </div>
              )}
            </div>
          </div>
        </motion.button>
      </motion.li>
    );
  }

  return (
    <motion.div 
      layout="position"
      variants={variants}
      whileHover={{ y: -2, backgroundColor: darkMode ? '#201e2b' : '#f8fafc' }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      onClick={() => isFolder && onClick(file)}
      aria-label={isFolder ? `Open folder ${file.name}` : `View file ${file.name}`}
      role={isFolder ? "button" : "document"}
      tabIndex={isFolder ? 0 : -1}
      onKeyDown={(e) => {
        if (isFolder && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick(file);
        }
      }}
      className={`w-full flex justify-between items-center border-b ${isFolder ? 'cursor-pointer' : ''} ${darkMode ? 'border-slate-800 bg-[#1a1823]' : 'border-slate-100 bg-white'}`}
    >
      <div className="pl-3 sm:pl-6 pr-2 sm:pr-3 py-3 flex items-start flex-1 min-w-0">
        <div className={`p-2 rounded-lg flex-shrink-0 ${darkMode ? (isFolder ? 'bg-blue-500/10' : 'bg-cyan-500/10') : (isFolder ? 'bg-blue-100' : 'bg-cyan-100')}`}>
          <DynamicIcon name={iconName} darkMode={darkMode} className="h-6 w-6" />
        </div>
        <div className="ml-3 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-medium whitespace-normal break-all leading-snug ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              {file.name}
            </span>
            {isLatest && (
              <span className="flex-shrink-0 px-2 py-1 rounded bg-[#20c747] text-[#07130a] text-xs font-semibold">
                LATEST
              </span>
            )}
          </div>
          {isDatedBuild && (
            <p className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {dateLabel}: {formatModifiedDate(displayedDate)}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center flex-shrink-0">
        <div className={`py-2 px-3 text-center w-28 flex-shrink-0 hidden sm:block ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {isFolder ? 'Folder' : getFileExtension(file.name)}
        </div>
        <div className={`py-2 px-3 text-center w-28 flex-shrink-0 hidden sm:block ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {formatBytes(file.size)}
        </div>
        
        {showActionsColumn && (
          <div className="pl-2 pr-3 sm:pl-3 sm:pr-6 py-2 w-16 sm:w-24 flex-shrink-0 flex justify-end">
            {!isFolder && (
              <DownloadButton
                darkMode={darkMode}
                onClick={onDownload}
                className={`
                  h-9 w-9 inline-flex items-center justify-center rounded-full transition-colors
                  ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}
                  hover:text-white
                `}
              />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
});

export default FileItem;
